import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Thermometer, Plane, Coins, Target } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo } from "@/i18n";
import { ZONES } from "@/data/zones";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { PropertyCard } from "@/components/PropertyCard";
import api from "@/api";

export function Zones() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="zones-page">
      <SEO title={`${t.home.zonesTitle} — ${t.brand}`} description={t.home.zonesSub} lang={lang} pathKey="zones" />
      <Reveal>
        <p className="eyebrow">{t.nav.zones}</p>
        <h1 className="h2-serif mt-3">{t.home.mapTitle}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.zonesSub}</p>
      </Reveal>
      <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {ZONES.map((z, i) => (
          <Reveal key={z.slug} delay={(i % 3) * 0.08}>
            <Link to={linkTo(lang, "zone", z.slug)} className="group relative block overflow-hidden rounded-3xl h-80" data-testid={`zones-card-${z.slug}`}>
              <img src={z.img} alt={`${z.name} — ${z[lang].tagline}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328]/85 via-[#1F2328]/20 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#E8B64C]">{z[lang].profile}</p>
                <h2 className="font-serif text-2xl text-[#F6EFE4] mt-1">{z.name}</h2>
                <p className="text-sm text-[#F6EFE4]/75 mt-1">{z[lang].tagline}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function ZoneDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = T[lang];
  const z = ZONES.find((x) => x.slug === slug);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (z) api.get("/listings", { params: { zone: z.slug } }).then((r) => setListings(r.data)).catch(() => {});
  }, [z]);

  if (!z) {
    return (
      <div className="container-site py-40 text-center">
        <h1 className="h2-serif">{t.notFound.title}</h1>
        <Link to={linkTo(lang, "zones")} className="btn-primary mt-8">{t.common.back}</Link>
      </div>
    );
  }
  const rows = [
    { icon: Plane, label: t.tools.compareRows.access, value: z[lang].access },
    { icon: Thermometer, label: t.tools.compareRows.climate, value: z[lang].climate },
    { icon: Coins, label: t.tools.compareRows.price, value: z[lang].price },
    { icon: Target, label: t.tools.compareRows.best, value: z[lang].best },
  ];
  return (
    <div data-testid="zone-detail-page">
      <SEO title={`${z.name} — ${t.home.zonesTitle} | ${t.brand}`} description={z[lang].tagline} lang={lang} pathKey="zone" slug={z.slug} />
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={z.img} alt={`${z.name} — ${z[lang].tagline}`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328]/85 via-[#1F2328]/25 to-transparent" />
        <div className="container-site absolute bottom-0 left-0 right-0 pb-12">
          <Link to={linkTo(lang, "zones")} className="inline-flex items-center gap-2 text-sm text-[#F6EFE4]/80 hover:text-white"><ArrowLeft size={15} /> {t.nav.zones}</Link>
          <p className="eyebrow !text-[#E8B64C] mt-4">{z[lang].profile}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F6EFE4] mt-2" data-testid="zone-title">{z.name}</h1>
          <p className="text-[#F6EFE4]/85 mt-2 text-lg">{z[lang].tagline}</p>
        </div>
      </section>
      <div className="container-site py-16 md:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <Reveal>
            <div className="space-y-4 text-[#1F2328]/80 leading-relaxed text-lg" data-testid="zone-description">
              {z[lang].description.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
          <Reveal className="mt-10 grid sm:grid-cols-2 gap-4">
            {rows.map((r) => (
              <div key={r.label} className="card-site p-5">
                <r.icon size={20} className="text-[#B5472B]" />
                <p className="label-site mt-3 !mb-1">{r.label}</p>
                <p className="text-sm text-[#1F2328]/80 leading-relaxed">{r.value}</p>
              </div>
            ))}
          </Reveal>
        </div>
        <aside className="h-fit lg:sticky lg:top-24 rounded-3xl bg-[#0E4D5C] text-[#F6EFE4] p-7">
          <h2 className="font-serif text-2xl">{t.home.ctaTitle}</h2>
          <p className="text-sm text-[#F6EFE4]/75 mt-3 leading-relaxed">{t.home.ctaSub}</p>
          <Link to={linkTo(lang, "contact")} className="btn-primary mt-6 w-full" data-testid="zone-cta">{t.nav.cta} <ArrowRight size={15} /></Link>
        </aside>
      </div>
      {listings.length > 0 && (
        <section className="bg-white/60 py-16">
          <div className="container-site">
            <h2 className="h2-serif !text-3xl">{t.nav.properties} — {z.name}</h2>
            <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {listings.map((item) => <PropertyCard key={item.ref_code} item={item} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
